import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import SearchIcon from '@material-ui/icons/Search'
import { useEffect } from 'react'

import { TEMPLATES_QUERY } from '../../graphql/contractTemplates'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import { getEntityCode } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { TemplateBrief } from '../../types/contractTemplate'
import { ProviderBrief } from '../../types/provider'
import useStyles from './styles/ContractsFilters.styles'

interface ContractsFiltersProps {
  setAutoCompleteForProviderCode: (rt: any) => void
  autoCompleteForProviderCode: any
  template: any
  setTemplate: (rt: any) => void
  inputValue: string | undefined
  setinputVal: (val: string) => void
  hasAccessToChangeProvider: boolean | undefined
  isRoleProvider: boolean
}

const ContractsFilters: React.FC<ContractsFiltersProps> = ({
  setAutoCompleteForProviderCode,
  template,
  autoCompleteForProviderCode,
  inputValue,
  setTemplate,
  setinputVal,
  hasAccessToChangeProvider,
  isRoleProvider
}) => {
  const classes = useStyles()

  const [getProviders, { data: providerData, loading: loadingForProvider }] =
    useLazyQuery<{
      providers: ProviderBrief[]
    }>(PROVIDERS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  const [
    getContractTemplates,
    { data: templates, loading: loadingForTemplatesList }
  ] = useLazyQuery<{
    templates: TemplateBrief[]
  }>(TEMPLATES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (hasAccessToChangeProvider) {
      getProviders()
    }
  }, [])

  useEffect(() => {
    if (autoCompleteForProviderCode || isRoleProvider) {
      getContractTemplates({
        variables: {
          providerCode: isRoleProvider
            ? getEntityCode()
            : autoCompleteForProviderCode?.value
        }
      })
    }
  }, [autoCompleteForProviderCode, getEntityCode()])

  return (
    <div
      className={[
        classes.root,
        !hasAccessToChangeProvider ? classes.rootForProviderRole : ''
      ].join(' ')}>
      {hasAccessToChangeProvider && (
        <div>
          <AutocompleteWithLabel
            placeholder={getTranslate('Please select a provider')}
            value={autoCompleteForProviderCode}
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) => option.value === value.value}
            options={
              providerData?.providers?.map((item) => ({
                label: `${item.name} - (${item.displayProviderCode})`,
                value: item.providerCode,
                id: item.id
              })) || []
            }
            onChange={(_, value: any) => {
              if (value) {
                setAutoCompleteForProviderCode({
                  value: value.value,
                  label: value.label,
                  id: value.id
                })
              } else {
                setAutoCompleteForProviderCode(null)
              }
              setinputVal('')
              setTemplate(null)
            }}
            fullWidth
            noHelperText
            inputSize="small"
            loading={loadingForProvider}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
      )}
      <div>
        <AutocompleteWithLabel
          placeholder={getTranslate('Please select a template')}
          value={template}
          getOptionLabel={(option) => option?.label || ''}
          getOptionSelected={(option, value) => option?.value === value?.value}
          options={
            templates?.templates?.map((item) => ({
              label: item.Title,
              value: item.Id || ''
            })) || []
          }
          onChange={(_, value: any) => {
            if (value) {
              setTemplate({
                value: value.value,
                label: value.label
              })
            } else {
              setTemplate(null)
            }
            setinputVal('')
          }}
          fullWidth
          noHelperText
          inputSize="small"
          loading={loadingForTemplatesList}
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
      <div>
        <InputWithLabel
          placeholder={getTranslate('Search in contracts names and Channels')}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          inputSize="small"
          value={inputValue}
          onChange={(event) =>
            setinputVal(
              convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
            )
          }
          disabled={loadingForProvider || loadingForTemplatesList}
        />
      </div>
    </div>
  )
}

export default ContractsFilters
